import { h, Component } from 'preact';
import Store from 'store/store';
import * as actions from 'store/actions';
import pure from 'utils/pure';
import { track } from 'utils/tracking';
import CanvasVideoPlayer from 'components/shared/simpleVideoPlayer/canvasVideoPlayer.js';
import PlayerControls from 'components/shared/simpleVideoPlayer/playerControls.js';
import s from 'components/shared/simpleVideoPlayer/simpleVideoPlayer.sss';
import { condClass, joinClasses } from 'utils/utils';
import Youtube from 'components/shared/youtube/youtube';
/**
 * Simplistic video element with interaction
 * @prop  {Boolean} enableMobileSoundIOS - do we need to be able to listen to sound on mobile
 * @prop  {Boolean} disableGUI - don't show the ui
 * @prop  {Boolean} disableInteraction - don't interact with it
 * @prop  {Boolean} forceIOSInline - enable the canvas inline playing hack for ios
 * @prop  {Function} tracking - callback
 * @prop  {Function} enforceFullScreen - function gets called when going into fullscreen mode and exiting it
 * @prop  {String} parentClass - the parent class for the wrapping div
 * @prop  {Boolean} showControls - show the player controls
 */
export default @pure class SimpleVideoPlayer extends Component {

	constructor(props) {
		super(props);

		this.client = Store.get().client || {};

		this.state.muted = true;
		this.state.captions = false;
		this.state.isMobile = this.client.deviceSize === 'mobile';
		this.state.isIOS = this.client && this.client.isIOS;
		this.state.loading = !!props.autoplay && !this.state.isMobile;
		this.state.playing = this.state.loading;
		this.state.showSlowLoadImage = false;
		this.playedOnce = false;
		this.totalWatchTime = 0;
		this.tracked = {};
		this.videoName = this.props.src?.split('\\').pop()?.split('/').pop();

		this.componentWillUnmount = this.componentWillUnmount.bind(this);
		this.forceAutoplay = this.forceAutoplay.bind(this);
		this.setProgress = this.setProgress.bind(this);
		this.play = this.play.bind(this);
		this.pause = this.pause.bind(this);
		this.toggleMute = this.toggleMute.bind(this);
		this.videoPlaying = this.videoPlaying.bind(this);
		this.videoPaused = this.videoPaused.bind(this);
		this.setActiveCaptionIndex = this.setActiveCaptionIndex.bind(this);
	}

	componentDidMount() {
		if (this.hideLoader) {
			this.setState({ loading: false });
		}

		if (!!this.props.slowLoad && !this.videoCanPlay) {
			this.slowLoadTimer = setTimeout(() => {
				this.setState({
					showSlowLoadImage: true,
					loading:           false
				});
			}, 5000);
		}

		this.$video = this.getVideoRef();
		// Add in seperate event handlers so we can still use the react events in the parent
		if (this.$video) {
			if (this.props.fromStart) {
				this.$video.currentTime = 0;
			}

			this.$video.addEventListener('canplay', this.videoCanPlayHandler);
			this.$video.addEventListener('playing', this.videoPlaying);
			// this.$video.addEventListener('waiting', this.videoWaiting);
			this.$video.addEventListener('pause', this.videoPaused);
			this.$video.addEventListener('ended', this.videoEnded);

			// Video already loaded, have to call the callback manually
			if (this.$video.readyState) {
				this.videoCanPlayHandler();
			}
		}
		window.addEventListener('beforeunload', this.componentWillUnmount);

		//this.forceAutoplay();

	}

	componentWillUpdate(nextProps) {
		const mutedPropChanged = this.props.muted != nextProps.muted;

		if (mutedPropChanged) {
			if (this.$video && typeof nextProps.muted !== 'object') {
				if (this.$video.muted != nextProps.muted) {
					this.setMute(nextProps.muted);
				}
			}
		}
	}

	componentDidUpdate(prevProps) {
		this.$video = this.getVideoRef();
		let playing = this.props.playing;
		if (typeof playing === 'boolean' && playing != prevProps.playing && playing != this.state.playing) {
			let func = this.props.playing ? this.play : this.pause;
			func();
		}
	}

	componentWillUnmount() {
		clearTimeout(this.playingCheck);

		if (this.$video) {
			this.$video.removeEventListener('canplay', this.videoCanPlayHandler);
			this.$video.removeEventListener('playing', this.videoPlaying);
			// this.$video.removeEventListener('waiting', this.videoWaiting);
			this.$video.removeEventListener('pause', this.videoPaused);
			this.$video.removeEventListener('ended', this.videoEnded);
		}
		window.addEventListener('beforeunload', this.componentWillUnmount);

		// Stop the tracker from running
		cancelAnimationFrame(this.timer);
		this.trackProgress = false;

		if (!this.unmounted && this.props.unmountTrack) {
			const videoCurrentTime = this.$video.currentTime;
			const videoDuration = this.$video.duration;
			this.props.unmountTrack(videoDuration, videoCurrentTime);
		}

		if (!this.unmounted && this.props.useTracking) {
			track({
				a: (this.props.trackPrefix || 'video') + '-watch-duration',
				l: this.totalWatchTime,
				isBeacon: true
			});
		}

		if (this.props.unmountResetVideoProgress) {
			this.$video.currentTime = 0;
		}

		this.unmounted = true;
	}


	forceAutoplay() {
		/*
			NOTE :
			this nasty piece of work is to get videos to play on samsung mobile devices.
			If a better solution has become apparent THEN DEAR GOD, PLEASE REPLACE THIS.
		*/

		const isMobile = this.client.deviceSize === 'mobile';

		if (!isMobile) {
			return;
		}
		let check = () => {
			const {
				autoplay,
				muted,
				src
			} = this.props;
			const video = this.getVideoRef();

			// if the video is allowed to play and meant to be playing...
			if (autoplay && muted && video && src) {
				// force the fricking thing to play if it isn't already
				if (video.paused) {
					video.play();
				}
				this.playingCheck = setTimeout(check, 1000);
			}
		};
		this.playingCheck = setTimeout(check, 1000);
	}

	/**
	 * Parent Interactions
	 */

	getVideoRef() {
		const videoComp = this.$videoComp;
		return videoComp && (videoComp.$video || videoComp.base);
	}

	/**
	 * Event Handlers
	 */

	setProgress(currentTime) {
		// Update the current time in the video
		this.setState({ progress: currentTime });
		this.$video.currentTime = currentTime;
	}

	progressTracker = () => {
		if (!this.trackProgress) {
			// We shouldn't be tracking the progress, so lets quit
			return;
		}

		if (this.$video) {
			// update the progress
			this.totalWatchTime += this.$video.currentTime - (this.state.progress || 0);
			// Only update the state progress if displaying the controls
			if (this.props.showControls) {
				this.setState({ progress: this.$video.currentTime });
			}
		}

		if (this.props.useTracking) {
			const position = this.$video.currentTime / this.$video.duration;
			[25, 50, 75, 95].forEach(v => {
				if (this.tracked[v]) return;
				if (position > v / 100) {
					Store.emit(actions.VIDEO_PLAYER_PROGRESS, v, this.videoName, this.props.isClosableTrailer);
					if (v !== 95) {
						track({ a: (this.props.trackPrefix || 'video') + '-past-' + v + (this.props.percentSuffix || '') });
					}
					this.tracked[v] = true;
				}
			});
		}

		this.timer = requestAnimationFrame(this.progressTracker);
	}

	videoPlaying() {
		if (this.props.useTracking && !this.tracked[0]) {
			Store.emit(actions.VIDEO_PLAYER_PROGRESS, 0, this.videoName, this.props.isClosableTrailer);
			this.tracked[0] = true;
		}

		let state = this.state;
		let newState = {
			playing:  true,
			loading:  false,
			duration: this.$video.duration,
			muted:    this.$video.muted
		};
		let actualNewState = Object.keys(newState).reduce((o, k) => {
			let val = newState[k];
			if (state[k] !== val) {
				o[k] = newState[k];
			}
			return o;
		}, {});
		if (Object.keys(actualNewState).length) {
			this.setState(actualNewState);
		}

		if (typeof this.props.onPlay === 'function') {
			this.props.onPlay();
		}

		if (!this.trackProgress) {
			this.trackProgress = true;
			// Start tracking the progress
			cancelAnimationFrame(this.timer);
			this.timer = requestAnimationFrame(this.progressTracker);
		}
		if (this.props.useTracking) {
			track({ a: (this.props.trackPrefix || 'video') + '-' + (!this.tracked.start ? 'started' : 'unpaused') });
			this.tracked.start = true;
		}
	}

	videoCanPlayHandler = () => {
		if (this.videoCanPlay) {
			return;
		}

		this.videoCanPlay = true;
		this.setState({
			playing: !this.$video.paused,
			loading: false
		});

		if (typeof this.props.canPlayCallback === 'function') {
			this.props.canPlayCallback(this.$video);
		}
	}

	videoWaiting = () => {
		// Make it async so we're sure it's actually paused and not just restarting the video
		setTimeout(() => {
			if (this.$video && this.$video.paused) {
				this.setState({
					playing: false,
					loading: true
				});
				// Stop tracking progress
				cancelAnimationFrame(this.timer);
				this.trackProgress = false;
			}
		}, 0);
	}

	videoPaused() {
		// Stop tracking progress
		cancelAnimationFrame(this.timer);
		this.trackProgress = false;

		if (typeof this.props.onPause === 'function') {
			this.props.onPause();
		}

		this.setState({
			playing: false,
			loading: false
		});

		if (this.props.useTracking) {
			track({ a: (this.props.trackPrefix || 'video') + '-paused' });
		}
	}

	videoEnded = () => {
		if (typeof this.props.onVideoEnded === 'function') {
			this.props.onVideoEnded();
		}

		if (this.props.useTracking) {
			track({ a: (this.props.trackPrefix || 'video') + '-completed' });
		}
	}

	togglePlay = () => {
		if (this.props.disableInteraction) {
			return;
		}

		if (!this.state.playing) {
			this.play();
		} else {
			this.pause();
		}
	}

	setActiveCaptionIndex(i) {
		this.setState({
			activeCaptionIndex: i
		});
	}

	toggleMute() {
		this.setMute(!this.state.muted);
	}

	setMute = (muted) => {
		this.$video.muted = muted;
		this.setState({ muted });
	}

	play() {
		if (this.state.playing) {
			return;
		}
		if (typeof this.props.track === 'function') {
			this.props.track('video-click-play-' + this.props.src.split('/').pop().replace('.mp4', ''));
		}

		if (this.$video && this.$video.paused) {
			this.$video.play();
		}
	}

	pause() {
		//POOK : added mobile check as this bugs out when pausing on desktop
		if (!this.state.playing && this.state.isMobile) {
			return;
		}
		if (typeof this.props.track === 'function') {
			this.props.track(`video-click-pause-${this.props.src.split('/').pop().replace('.mp4', '')}`);
		}

		if (this.$video && !this.$video.paused) {
			this.$video.pause();
		}
	}

	render(props) {
		// Keep custom props here even if not used to avoid putting them on the final video element (remainingProps)
		let {
			enableMobileSoundIOS,
			disableGUI,
			parentClass,
			slowLoad,
			controlsClass,
			controlsContainerStyle,
			useTracking,
			overrideHideControls,
			noClickToggle,
			noPausedButton,
			controlsProps = {}, // props to pass to the controls
			disableInteraction,
			showControls,
			HTMLTag = 'div',
			youtubePlayer,
			...remainingProps
		} = props;

		if (this.videoCanPlay) {
			clearTimeout(this.slowLoadTimer);
		}
		const extraProps = {};
		let hideControls = false;
		let videoPlayer = null;

		if (this.state.isIOS && !this.props.overrideHideControls) {
			// hide the controls when on ios for now
			hideControls = true;
		}

		const onClick = !noClickToggle && this.togglePlay;

		if (!this.props.enableMobileSoundIOS) {
			if (this.state.isIOS && this.props.forceIOSInline) {
				this.hideLoader = true;
				videoPlayer = <CanvasVideoPlayer key={this.props.src} src={this.props.src} class={joinClasses(s.videoElement, this.props.class)} onClick={onClick} />;
			} else {
				extraProps.playsinline = true;
			}
		}

		let videoClass = s.video;
		if (!this.state.playing && !this.props.disableGUI && this.state.isMobile) {
			videoClass = s.videoPaused;
		}
		if (this.state.loading) {
			videoClass = s.videoLoading;
		}

		if (!videoPlayer) {
			// Check the defined muted state for a specific device
			if (remainingProps && typeof remainingProps.muted === 'object') {
				const muted = remainingProps.muted[this.client.device];
				remainingProps.muted = muted !== undefined ? muted : false;
			}

			videoPlayer = (
				<BasicVideo
					class={joinClasses(s.videoElement, this.props.class, this.props.className)}
					key={this.props.src}
					paused={!this.state.playing}
					activeCaptionIndex={this.state.activeCaptionIndex}
					isMobile={this.state.isMobile}
					ref={$ref => $ref && (this.$videoComp = $ref)}
					onClick={onClick}
					{...remainingProps}
					{...extraProps}
					captions={this.props.captions}
				/>
			);
		}

		if (youtubePlayer) {
			videoPlayer = (
				<Youtube
					ref={$ref => $ref && (this.$videoComp = $ref)}
					class={joinClasses(s.videoElement, this.props.class, this.props.className)}
					videoId={this.props.src}
					key={`youtube-${this.props.src}`}
				/>
			);
		}

		showControls = showControls && !hideControls;
		const noButton = noPausedButton || showControls;
		return (
			<HTMLTag
				class={joinClasses(videoClass, this.props.parentClass, noButton && s.noButton, this.videoCanPlay && s.videoCanPlay)}
				ref={$ref => $ref && (this.$container = $ref)} key={this.props.src}
			>
				{this.state.showSlowLoadImage ? slowLoad() : videoPlayer}
				{showControls && (
					<PlayerControls
						key="controls"
						containerClass={s.controlsContainer}
						ref={e => e && (this.$controls = e)}
						playing={this.state.playing}
						togglePlay={this.togglePlay}
						captions={this.props.captions}
						setActiveCaptionIndex={this.setActiveCaptionIndex}
						activeCaptionIndex={this.state.activeCaptionIndex}
						duration={this.state.duration}
						progress={this.state.progress}
						muted={this.state.muted}
						toggleMute={this.toggleMute}
						containerRef={this.$container}
						setProgress={this.setProgress}
						enforceFullScreen={this.props.enforceFullScreen}
						passedClass={s.playerControls + condClass(controlsClass)}
						containerStyle={controlsContainerStyle}
						{...controlsProps}
					/>
				)}
			</HTMLTag>
		);
	}

}

// Lets use this simple video so we can actually se state in the player to rerender
class BasicVideo extends Component {

	constructor() {
		super();
		this.state.showSlowLoadImage = false;
		this.client = Store.get().client;
	}

	componentDidMount() {
		if (this.$video) {
			if (this.props.captions && this.props.captions.length) {
				this.setCueVisibility(this.props.activeCaptionIndex);
				this.props.captions.forEach((c, i) => this.setCuePosition(i));
			}
			if (typeof this.props.onPlayerReady === 'function') {
				// added setTimeout to allow opacity transition on closable Trailer - Netflix Calico Cut Pants
				setTimeout(this.props.onPlayerReady, 50);
			}
			if (this.props.autoplay && !this.props.isMobile && this.$video.paused) {
				this.$video.play();
			}
		}
	}

	componentDidUpdate(prevProps) {
		let { captions, activeCaptionIndex } = this.props;
		if (this.$video && captions && captions.length) {
			if (activeCaptionIndex !== prevProps.activeCaptionIndex) {
				this.setCueVisibility(activeCaptionIndex);
			}
		}
	}

	componentWillReceiveProps(nextProps) {
		if (!!nextProps.muted !== !!this.props.muted && this.$video) {
			this.$video.muted = !!nextProps.muted;
		}
	}

	shouldComponentUpdate(nextProps) {
		const clickUpdated = nextProps.click !== this.props.onClick;
		const srcUpdated = nextProps.src !== this.props.src;

		return clickUpdated || srcUpdated;
	}

	setCuePosition = (i) => {
		if (i === undefined) {
			return;
		}
		const textTrack = this.$video.textTracks[i];
		const cuePosition = this.props.cuePosition;

		if (textTrack.cues && textTrack.cues.length) {
			Object.values(textTrack.cues).forEach((cue) => {
				if (this.client.deviceSize === 'desktop') {
					cue.line = cuePosition || 24;
				}
				if (this.client.deviceSize === 'mobile' && this.client.orientation === 'landscape') {
					cue.line = cuePosition || 18;
				}
				if (this.client.deviceSize === 'mobile' && this.client.orientation === 'portrait') {
					cue.line = cuePosition || 30;
				}
			});
		} else {
			setTimeout(() => this.setCuePosition(i), 0);
		}
	}

	setCueVisibility = (index) => {
		const textTracks = this.$video.textTracks;
		textTracks?.forEach(t => t.mode = 'hidden');

		if ((index || index === 0) && textTracks?.[index]) {
			textTracks[index].mode = 'showing';
		}
	}

	render({ isMobile, onPlayerReady, ...remainingProps }) {
		delete remainingProps.captionsSrc;
		delete remainingProps.captionsLang;
		delete remainingProps.captionsLabel;

		const { captions } = this.props;

		return (
			<video
				ref={$ref => this.$video = $ref}
				webkit-playsinline playsinline
				{...remainingProps}
				crossorigin="anonymous"
			>
				{captions && captions.length && captions.map(caption => (
					<track
						label={caption.label}
						src={caption.src}
						kind={caption.kind}
						srclang={caption.lang}
					/>
				))
				}
			</video>
		);
	}

}
